/**
 * @typedef {object} StatGroup - player stats or team stats object, ex: { totalYards: 10 }
 * @typedef {object} Play - current play
 * @typedef {string} StatName - exammples: "passingYards", "penalties"
 * @typedef {("home"|"away")} Side
 * @typedef {("player1"|"player2"|"teamOffense"|"teamDefense")} Type - getting stats for player1, player2, or team
 */

import _ from "lodash";
import { isDisplayable, isTruthyOrZero } from "util/misc";
import { getTeamSideFromId } from "games/utils";

/*
 * @param {StatGroup} item
 * @param {Play} play
 * @param {StatName} statName
 * @param {boolean} abs - always return absolute value
 * @returns {number}
 */
const addYardsStat = (item, play, statName, abs = false) => {
  const rawLengthOfPlay = _.get(play, "lengthOfPlay", 0) || 0;
  const lengthOfPlay = abs ? Math.abs(rawLengthOfPlay) : rawLengthOfPlay;
  const rawYards = _.get(item, statName, 0) || 0;
  const yards = abs ? Math.abs(rawYards) : rawYards;
  return yards + lengthOfPlay;
};

/*
 * @param {StatGroup} item
 * @param {StatName} statName
 * @returns {number}
 */
const addStat = (item, statName) => {
  const statValue = _.get(item, statName, 0) || 0;
  return statValue + 1;
};

/*
 * @param {Type} type
 * @param {Play} play
 * @param {object} game
 * @param {StatGroup} item
 * @returns {number}
 */
const getPlayerTotalPoints = ({ type, play, game, item }) => {
  const {
    playType,
    teamInPossessionId,
    includesScore,
    includesTouchdown
  } = play;
  if (playType === "safety") return;

  const teamInPossessionSide = getTeamSideFromId(game, teamInPossessionId);
  const isPassingPlay = playType === "pass" || playType === "PATPass";
  const isReceiver = type === "player2";
  const playerCheck = (isPassingPlay && isReceiver) || !isPassingPlay;
  const pointsGained =
    _.get(play, [`${teamInPossessionSide}PointsGained`], 0) || 0;
  const pointsHad = _.get(item, "totalPoints", 0) || 0;
  const totalPoints = pointsHad + pointsGained;

  return playerCheck && (includesScore || includesTouchdown)
    ? totalPoints
    : null;
};

/*
 * @param {Play} play
 * @param {Side} side
 * @param {StatGroup} item
 * @returns {object}
 */
const getQuarterPoints = ({ play, side, item }) => {
  const pointsMap = {
    1: "firstQuarterPoints",
    2: "secondQuarterPoints",
    3: "thirdQuarterPoints",
    4: "fourthQuarterPoints",
    5: "overtimePoints",
    6: "overtime2Points",
    7: "overtime3Points"
  };

  const pointsGained = _.get(play, [`${side}PointsGained`], 0) || 0;
  const quarter = _.get(play, "quarter");
  const quarterString = quarter ? quarter.toString() : "";
  const currentQuarterKey = _.get(pointsMap, quarterString);
  const pointsHad = _.get(item, [currentQuarterKey], 0) || 0;
  const quarterPoints = pointsHad + pointsGained;
  return { key: currentQuarterKey, value: quarterPoints };
};

/*
 * @param {[string]} fieldNames - mapped stat names for this play
 * @param {StatGroup} item
 * @param {Play} play
 * @param {object} game
 * @param {Type} type
 * @param {Side}
 * @returns {object}
 */
const getChanges = ({
  fieldNames = [],
  item,
  play,
  game,
  type,
  side = null
}) => {
  const { includesScore, includesTouchdown, fumble, convertsFirstDown } = play;

  const addYardsStatIf = (statName, abs = false) => {
    if (!fieldNames.includes(statName)) return;
    return addYardsStat(item, play, statName, abs);
  };

  const addStatIf = (statName) => {
    if (!fieldNames.includes(statName)) return;
    return addStat(item, statName);
  };

  const quarterPoints =
    (type === "teamOffense" || type === "teamDefense") &&
    getQuarterPoints({ play, side, item });
  const quarterPointsKey = _.get(quarterPoints, "key");
  const quarterPointsValue = _.get(quarterPoints, "value");

  const values = {
    penaltyYards: addYardsStatIf("penaltyYards", true),
    penalties: addStatIf("penalties"),
    passingYards: addYardsStatIf("passingYards"),
    passingAttempts: addStatIf("passingAttempts"),
    passingCompletions: addStatIf("passingCompletions"),
    passingInterceptions: addStatIf("passingInterceptions"),
    passingTouchdowns: includesTouchdown && addStatIf("passingTouchdowns"),
    rushingYards: addYardsStatIf("rushingYards"),
    rushingAttempts: addStatIf("rushingAttempts"),
    rushingTouchdowns: includesTouchdown && addStatIf("rushingTouchdowns"),
    receptions: addStatIf("receptions"),
    receivingYards: addYardsStatIf("receivingYards"),
    receivingTouchdowns: includesTouchdown && addStatIf("receivingTouchdowns"),
    fieldGoalYards: includesScore && addYardsStatIf("fieldGoalYards"),
    fieldGoalAttempts: addStatIf("fieldGoalAttempts"),
    fieldGoalsMade: includesScore && addStatIf("fieldGoalsMade"),
    puntingYards: addYardsStatIf("puntingYards"),
    pointAfterTouchdownAttempts: addStatIf("pointAfterTouchdownAttempts"),
    pointAfterTouchdownsMade:
      includesScore && addStatIf("pointAfterTouchdownsMade"),
    punts: addStatIf("punts"),
    passingInterceptionYards: addYardsStatIf("passingInterceptionYards"),
    // team-specific stats that are not based on playType
    fumbles:
      type === "teamOffense" &&
      isDisplayable(fumble) &&
      addStat(item, "fumbles"),
    fumblesLost:
      type === "teamOffense" &&
      fumble === "lost" &&
      addStat(item, "fumblesLost"),
    firstDowns:
      type === "teamOffense" &&
      convertsFirstDown &&
      addStat(item, "firstDowns"),
    [quarterPointsKey]: quarterPointsValue,
    // player-specific stats that are not based on playType
    totalPoints:
      (type === "player1" || type === "player2") &&
      getPlayerTotalPoints({ type, play, game, item })
  };

  const fieldCheck = (statName) => {
    if (!statName) return;
    let statValue = _.get(values, statName);
    // sets value to 0 instead of null / undefined
    if (!isTruthyOrZero(statValue)) {
      statValue = _.get(item, statName, 0) || 0;
    }
    return { [statName]: statValue };
  };

  return {
    ...fieldCheck("passingYards"),
    ...fieldCheck("passingAttempts"),
    ...fieldCheck("passingCompletions"),
    ...fieldCheck("passingInterceptions"),
    ...fieldCheck("passingTouchdowns"),
    ...fieldCheck("rushingYards"),
    ...fieldCheck("rushingAttempts"),
    ...fieldCheck("rushingTouchdowns"),
    ...fieldCheck("receptions"),
    ...fieldCheck("receivingYards"),
    ...fieldCheck("receivingTouchdowns"),
    ...fieldCheck("fieldGoalYards"),
    ...fieldCheck("fieldGoalAttempts"),
    ...fieldCheck("fieldGoalsMade"),
    ...fieldCheck("puntingYards"),
    ...fieldCheck("fumbles"),
    ...fieldCheck("fumblesLost"),
    ...fieldCheck("pointAfterTouchdownAttempts"),
    ...fieldCheck("pointAfterTouchdownsMade"),
    ...fieldCheck("firstDowns"),
    ...fieldCheck("punts"),
    ...fieldCheck("passingInterceptionYards"),
    ...fieldCheck("penalties"),
    ...fieldCheck("penaltyYards"),
    ...fieldCheck("totalPoints"),
    ...fieldCheck(quarterPointsKey)
  };
};

export { addStat, addYardsStat };
export default getChanges;
